import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Avatar,
  Box,
  Burger,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCar,
  IconDownload,
  IconLogin2,
  IconLogout,
  IconSettings,
  IconUserCircle,
  IconUserPlus,
} from '@tabler/icons-react';

import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/lib/useAuth';
import classes from './Header.module.css';

interface NavItem {
  label: string;
  to: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Download', to: '/download' },
  { label: 'Analyzer', to: '/analyzer' },
  { label: 'Community', to: '/community' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'About', to: '/about' },
  { label: 'Blog', to: '/blog' },
] as const;

function initials(name: string): string {
  const parts = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '');
  return parts.join('') || '?';
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  return (
    <Menu position="bottom-end" shadow="md" width={220}>
      <Menu.Target>
        <UnstyledButton
          aria-label={`Account menu for ${user.display_name}`}
          className={classes.avatarTrigger}
        >
          <Avatar
            src={user.avatar_url ?? undefined}
            alt=""
            color="forge"
            radius="xl"
            size="md"
          >
            {initials(user.display_name)}
          </Avatar>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Text size="sm" fw={600}>
            {user.display_name}
          </Text>
          <Text size="xs" c="dimmed">
            @{user.username}
          </Text>
        </Menu.Label>
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconUserCircle size={16} />}
          onClick={() => navigate(`/profile/${user.username}`)}
        >
          View profile
        </Menu.Item>
        <Menu.Item
          leftSection={<IconSettings size={16} />}
          onClick={() => navigate('/settings/profile')}
        >
          Profile settings
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCar size={16} />}
          onClick={() => navigate('/settings/garage')}
        >
          My garage
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconLogout size={16} />}
          color="red"
          onClick={() => {
            void logout().then(() => navigate('/'));
          }}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, status } = useAuth();
  const isAuthed = status === 'authenticated' && user != null;

  return (
    <Box component="header" role="banner" className={classes.header}>
      <Container size="xl" className={classes.inner}>
        <Link to="/" aria-label="Clancy Tuning — home" className={classes.logoLink}>
          <Logo />
        </Link>

        <Box component="nav" role="navigation" aria-label="Primary" className={classes.desktopNav}>
          <Group gap="lg">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive ? `${classes.navLink} ${classes.navLinkActive}` : classes.navLink
                }
              >
                {item.label}
              </NavLink>
            ))}
          </Group>
        </Box>

        <Group gap="sm" className={classes.actions}>
          <Button
            component={Link}
            to="/download"
            variant="gradient"
            gradient={{ from: 'var(--tf-forge-start)', to: 'var(--tf-forge-end)', deg: 135 }}
            leftSection={<IconDownload size={18} />}
            radius="md"
            visibleFrom="md"
          >
            Download Free
          </Button>
          {isAuthed ? (
            <Box visibleFrom="md">
              <AccountMenu />
            </Box>
          ) : (
            <Group gap="xs" visibleFrom="md">
              <Button
                component={Link}
                to="/login"
                variant="subtle"
                color="gray"
                radius="md"
                leftSection={<IconLogin2 size={18} />}
              >
                Sign in
              </Button>
              <Button
                component={Link}
                to="/register"
                variant="default"
                radius="md"
                leftSection={<IconUserPlus size={18} />}
              >
                Sign up
              </Button>
            </Group>
          )}
          <ActionIcon
            hiddenFrom="md"
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-haspopup="menu"
            // aria-controls is only meaningful when the controlled element is
            // in the DOM. Mantine's Drawer renders into a portal that only
            // exists while open, so we wire the relationship conditionally.
            aria-controls={mobileOpen ? 'tf-mobile-nav' : undefined}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <Burger opened={mobileOpen} size="sm" color="var(--tf-text-primary)" />
          </ActionIcon>
        </Group>
      </Container>

      <Drawer
        opened={mobileOpen}
        onClose={() => setMobileOpen(false)}
        position="right"
        size="80%"
        title={<Logo size="sm" />}
        aria-label="Mobile navigation"
        hiddenFrom="md"
      >
        {/* id lives on the inner nav element (in-portal) so the burger's
            aria-controls actually resolves while the drawer is open. The
            Drawer dialog already carries "Mobile navigation" as its label;
            this nested <nav> deliberately stays unlabeled so SR users
            don't see two redundant landmarks. */}
        <Stack id="tf-mobile-nav" gap="lg" mt="md" component="nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                isActive
                  ? `${classes.mobileLink} ${classes.mobileLinkActive}`
                  : classes.mobileLink
              }
              aria-current={location.pathname === item.to ? 'page' : undefined}
            >
              {item.label}
            </NavLink>
          ))}

          <Divider color="var(--tf-border)" />

          {isAuthed ? (
            <Stack gap="sm">
              <NavLink
                to={`/profile/${user.username}`}
                onClick={() => setMobileOpen(false)}
                className={classes.mobileLink}
              >
                View profile
              </NavLink>
              <NavLink
                to="/settings/profile"
                onClick={() => setMobileOpen(false)}
                className={classes.mobileLink}
              >
                Profile settings
              </NavLink>
              <NavLink
                to="/settings/garage"
                onClick={() => setMobileOpen(false)}
                className={classes.mobileLink}
              >
                My garage
              </NavLink>
            </Stack>
          ) : (
            <Stack gap="sm">
              <Button
                component={Link}
                to="/login"
                onClick={() => setMobileOpen(false)}
                variant="default"
                size="md"
                fullWidth
                leftSection={<IconLogin2 size={18} />}
              >
                Sign in
              </Button>
              <Button
                component={Link}
                to="/register"
                onClick={() => setMobileOpen(false)}
                variant="light"
                color="forge"
                size="md"
                fullWidth
                leftSection={<IconUserPlus size={18} />}
              >
                Create account
              </Button>
            </Stack>
          )}

          <Button
            component={Link}
            to="/download"
            onClick={() => setMobileOpen(false)}
            variant="gradient"
            gradient={{ from: 'var(--tf-forge-start)', to: 'var(--tf-forge-end)', deg: 135 }}
            leftSection={<IconDownload size={18} />}
            radius="md"
            size="md"
            fullWidth
          >
            Download Free
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}
